import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '@/lib/auth.tsx'
import { useToast } from '@/components/ui/use-toast'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { CheckCircle, XCircle, Loader2, Building2, User, Calendar, Mail } from 'lucide-react'
import { format } from 'date-fns'
import { Auth } from '@/components/public/auth/Auth'

interface InvitationDetails {
  valid: boolean
  brand_name?: string
  brand_id?: string
  access_level?: 'owner' | 'editor' | 'viewer'
  invited_by?: string
  email?: string
  message?: string
  expires_at?: string
  error?: string
}

export function AcceptBrandInvitation() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { session } = useAuth()
  const { toast } = useToast()
  const [loading, setLoading] = useState(true)
  const [accepting, setAccepting] = useState(false)
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null)
  const [accepted, setAccepted] = useState(false)

  useEffect(() => {
    if (token) {
      fetchInvitationDetails()
    }
  }, [token])

  const fetchInvitationDetails = async () => {
    try {
      // Import supabase here to avoid circular dependencies
      const { createClient } = await import('@supabase/supabase-js')
      const supabase = createClient(
        import.meta.env.VITE_SUPABASE_URL!,
        import.meta.env.VITE_SUPABASE_ANON_KEY!
      )
      
      // Use the public RPC function to get invitation details
      const { data, error } = await supabase.rpc('get_brand_invitation_details', {
        p_token: token!
      })

      if (error) {
        console.error('Error fetching invitation:', error)
        throw new Error(error.message)
      }

      setInvitation(data as unknown as InvitationDetails)
    } catch (error) {
      console.error('Error fetching invitation:', error)
      setInvitation({
        valid: false,
        error: 'Failed to load invitation details'
      })
    } finally {
      setLoading(false)
    }
  }

  const handleAcceptInvitation = async () => {
    if (!session) {
      // This shouldn't happen as the button is only shown for authenticated users
      toast({
        title: 'Authentication Required',
        description: 'Please sign in or create an account to accept this invitation.',
        variant: 'default'
      })
      return
    }
    
    if (!token) {
      toast({
        title: 'Error',
        description: 'No invitation token found.',
        variant: 'destructive'
      })
      return
    }

    setAccepting(true)
    try {
      // Import supabase client creation directly
      
      // Create an authenticated client with the current session
      const { createClient } = await import('@supabase/supabase-js')
      const authSupabase = createClient(
        import.meta.env.VITE_SUPABASE_URL!,
        import.meta.env.VITE_SUPABASE_ANON_KEY!,
        {
          auth: {
            persistSession: false
          },
          global: {
            headers: {
              Authorization: `Bearer ${session.access_token}`
            }
          }
        }
      )
      
      // Use the RPC function to accept the invitation
      const { data, error } = await authSupabase.rpc('accept_brand_invitation', {
        invitation_token: token
      })

      if (error) {
        throw new Error(error.message || 'Failed to accept invitation')
      }

      setAccepted(true)
      
      // Set onboarding as completed for invited users
      try {
        const { error: updateError } = await authSupabase
          .schema('marketing_and_customer_success')
          .from('profiles')
          .update({ onboarding_completed: true })
          .eq('id', session.user.id)
          
        if (updateError) {
          console.error('[AcceptBrandInvitation] Error updating profile:', updateError)
        } else {
          console.log('[AcceptBrandInvitation] Set onboarding_completed to true')
        }
      } catch (error) {
        console.error('[AcceptBrandInvitation] Error updating onboarding status:', error)
      }
      
      toast({
        title: 'Success!',
        description: `You now have ${data.access_level} access to ${data.brand_name}`
      })

      // Redirect to the brands page after a short delay
      setTimeout(() => {
        navigate('/brands')
      }, 2000)
    } catch (error) {
      console.error('Error accepting invitation:', error)
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to accept invitation',
        variant: 'destructive'
      })
    } finally {
      setAccepting(false)
    }
  }

  const getAccessLevelDescription = (level: string) => {
    switch (level) {
      case 'owner':
        return 'Full administrative access including managing other users'
      case 'editor':
        return 'Create and edit content, campaigns, and resources'
      case 'viewer':
        return 'View-only access to all brand content'
      default:
        return level
    }
  }

  const getAccessLevelColor = (level: string) => {
    switch (level) {
      case 'owner':
        return 'bg-purple-100 text-purple-800'
      case 'editor':
        return 'bg-blue-100 text-blue-800'
      case 'viewer':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
            <p className="text-muted-foreground">Loading invitation details...</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!invitation?.valid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center space-x-2">
              <XCircle className="h-6 w-6 text-red-500" />
              <CardTitle>Invalid Invitation</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              {invitation?.error || 'This invitation is invalid or has expired.'}
            </p>
          </CardContent>
          <CardFooter>
            <Button onClick={() => navigate('/')} variant="outline" className="w-full">
              Go to Home
            </Button>
          </CardFooter>
        </Card>
      </div>
    )
  }

  if (accepted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center space-x-2">
              <CheckCircle className="h-6 w-6 text-green-500" />
              <CardTitle>Invitation Accepted!</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              You now have access to <span className="font-semibold">{invitation.brand_name}</span>.
              Redirecting you to your brands...
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl">Brand Access Invitation</CardTitle>
          <CardDescription>
            You've been invited to collaborate on Rainfactory
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Brand Info */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <div className="flex items-start space-x-3">
              <Building2 className="h-5 w-5 text-gray-500 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-gray-500">Brand</p>
                <p className="font-semibold text-lg">{invitation.brand_name}</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <User className="h-5 w-5 text-gray-500 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-gray-500">Invited by</p>
                <p className="font-medium">{invitation.invited_by}</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <Mail className="h-5 w-5 text-gray-500 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-gray-500">Invitation for</p>
                <p className="font-medium">{invitation.email}</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <Calendar className="h-5 w-5 text-gray-500 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-gray-500">Expires</p>
                <p className="font-medium">
                  {invitation.expires_at ? format(new Date(invitation.expires_at), 'PPP') : 'Never'}
                </p>
              </div>
            </div>
          </div>

          {/* Access Level */}
          <div className="space-y-2">
            <p className="text-sm font-medium">Access Level</p>
            <div className="flex items-center space-x-2">
              <Badge className={getAccessLevelColor(invitation.access_level || '')}>
                {invitation.access_level ? invitation.access_level.charAt(0).toUpperCase() + invitation.access_level.slice(1) : ''}
              </Badge>
              <span className="text-sm text-muted-foreground">
                {getAccessLevelDescription(invitation.access_level || '')}
              </span>
            </div>
          </div>

          {/* Message */}
          {invitation.message && (
            <div className="space-y-2">
              <p className="text-sm font-medium">Message from {invitation.invited_by}</p>
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                <p className="text-sm">{invitation.message}</p>
              </div>
            </div>
          )}

          {/* User Check */}
          {session && session.user?.email !== invitation.email && (
            <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> You're logged in as {session.user.email}, but this invitation is for {invitation.email}.
                You may need to log in with the correct account.
              </p>
            </div>
          )}

          {/* Authentication for unauthenticated users */}
          {!session && (
            <>
              <div className="border-t pt-4">
                <p className="text-sm text-muted-foreground text-center mb-4">
                  Please sign in or create an account to accept this invitation
                </p>
              </div>
              <Auth 
                view="invite" 
                redirectTo={`/process-invitation?invitation_token=${token}`}
                hideLabels 
              />
            </>
          )}
        </CardContent>
        
        {session && (
          <CardFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAcceptInvitation}
              disabled={accepting}
              className="flex-1"
            >
              {accepting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Accepting...
                </>
              ) : (
                'Accept Invitation'
              )}
            </Button>
          </CardFooter>
        )}
      </Card>
    </div>
  )
}